import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../contexts/AuthContext';
import { Camera, X, Upload, Trash2, LogOut, Settings } from 'lucide-react';
import axios from 'axios';
import SettingsModal from './SettingsModal';


const API = "/api";

const ProfilePicture = ({ size = "medium", showDropdown = true, className = "" }) => {
  const { user, refreshUser, logout } = useAuth();
  const { toast } = useToast();
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showDropdownMenu, setShowDropdownMenu] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadPreview, setUploadPreview] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);

  const sizeClasses = {
    small: "w-8 h-8",
    medium: "w-10 h-10",
    large: "w-12 h-12",
    xlarge: "w-16 h-16"
  };

  const textSizeClasses = {
    small: "text-xs",
    medium: "text-sm",
    large: "text-base",
    xlarge: "text-lg"
  };

  const getProfileImageUrl = (user) => {
    if (user?.profile_picture_url) {
      if (user.profile_picture_url.startsWith('/api/')) {
        return user.profile_picture_url;
      }
      if (user.profile_picture_url.startsWith('/')) {
        return `${API}${user.profile_picture_url}`;
      }
      return user.profile_picture_url;
    }
    return user?.profile_image;
  };

  const profileImageUrl = getProfileImageUrl(user);

  const handleFileSelect = (file) => {
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast({
        title: "Error",
        description: "Please select a JPEG, PNG, GIF, or WebP image.",
        variant: "destructive"
      });
      return;
    }

    // File size limit removed - images can now be any size

    setUploadFile(file);

    const reader = new FileReader();
    reader.onload = (e) => {
      setUploadPreview(e.target.result);
    };
    reader.readAsDataURL(file);
  };

  const handleUpload = async () => {
    if (!uploadFile || !user?.id) return;

    setIsUploading(true);
    const formData = new FormData();
    formData.append('file', uploadFile);

    try {
      const response = await axios.post(
        `${API}/users/${user.id}/profile-picture`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      setUploadFile(null);
      setUploadPreview(null);
      setShowUploadModal(false);
      setShowDropdownMenu(false);

      toast({
        title: "Success",
        description: "Profile picture updated successfully!"
      });

      // Refresh user data to get updated profile picture
      await refreshUser();

    } catch (error) {
      console.error('Failed to upload profile picture:', error);
      toast({
        title: "Error",
        description: "Failed to upload profile picture. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsUploading(false);
    }
  };

  const handleRemove = async () => {
    if (!user?.id) return;

    try {
      await axios.delete(`${API}/users/${user.id}/profile-picture`);

      setUploadFile(null);
      setUploadPreview(null);
      setShowUploadModal(false);
      setShowDropdownMenu(false);

      toast({
        title: "Success",
        description: "Profile picture removed successfully!"
      });

      // Refresh user data to get updated profile picture
      await refreshUser();

    } catch (error) {
      console.error('Failed to remove profile picture:', error);
      toast({
        title: "Error",
        description: "Failed to remove profile picture. Please try again.",
        variant: "destructive"
      });
    }
  };

  const handleProfileClick = () => {
    if (showDropdown) {
      setShowDropdownMenu(!showDropdownMenu);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      setShowDropdownMenu(false);
      toast({
        title: "Success",
        description: "Logged out successfully",
      });
    } catch (error) {
      console.error('Logout error:', error);
      toast({
        title: "Error",
        description: "Failed to logout",
        variant: "destructive",
      });
    }
  };

  return (
    <div className={`relative ${className}`}>
      {/* Profile Picture */}
      <button
        onClick={handleProfileClick}
        className={`${sizeClasses[size]} rounded-full overflow-hidden border-2 border-slate-200 hover:border-blue-400 transition-colors relative group`}
        title={showDropdown ? "Click to change profile picture" : "Profile picture"}
      >
        {profileImageUrl ? (
          <img
            src={profileImageUrl}
            alt={user?.full_name}
            className="w-full h-full object-cover"
            onError={(e) => {
              e.target.style.display = 'none';
              e.target.nextSibling.style.display = 'flex';
            }}
          />
        ) : null}
        <div className={`${sizeClasses[size]} bg-gradient-to-r from-blue-500 to-indigo-500 flex items-center justify-center text-white ${textSizeClasses[size]} font-medium ${profileImageUrl ? 'hidden' : 'flex'}`}>
          {user?.full_name?.charAt(0) || '?'}
        </div>
        
        {/* Camera overlay on hover */}
        {showDropdown && (
          <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
            <Camera className="w-4 h-4 text-white" />
          </div>
        )}
      </button>

      {/* Dropdown Menu */}
      {showDropdown && showDropdownMenu && (
        <div className="absolute right-0 mt-2 w-64 bg-white rounded-lg shadow-lg border border-slate-200 z-50">
          <div className="p-4 border-b border-slate-200">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 rounded-full overflow-hidden border-2 border-slate-200">
                {profileImageUrl ? (
                  <img
                    src={profileImageUrl}
                    alt={user?.full_name}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                ) : null}
                <div className={`w-12 h-12 bg-gradient-to-r from-blue-500 to-indigo-500 flex items-center justify-center text-white text-sm font-medium ${profileImageUrl ? 'hidden' : 'flex'}`}>
                  {user?.full_name?.charAt(0) || '?'}
                </div>
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-slate-900 truncate">
                  {user?.full_name}
                </div>
                <div className="text-xs text-slate-500 truncate">
                  {user?.email}
                </div>
                <div className="text-xs text-slate-400 capitalize">
                  {user?.role}
                </div>
              </div>
            </div>
          </div>
          
          <div className="p-2">
            <button
              onClick={() => {
                setShowUploadModal(true);
                setShowDropdownMenu(false);
              }}
              className="w-full flex items-center space-x-2 px-3 py-2 text-sm text-slate-700 hover:bg-slate-100 rounded-md transition-colors"
            >
              <Upload className="w-4 h-4" />
              <span>Change Profile Picture</span>
            </button>
            
            {profileImageUrl && (
              <button
                onClick={handleRemove}
                className="w-full flex items-center space-x-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-md transition-colors"
              >
                <Trash2 className="w-4 h-4" />
                <span>Remove Picture</span>
              </button>
            )}
            
            <div className="border-t border-slate-200 my-1"></div>
            
            <button
              onClick={() => {
                setShowSettingsModal(true);
                setShowDropdownMenu(false);
              }}
              className="w-full flex items-center space-x-2 px-3 py-2 text-sm text-slate-700 hover:bg-slate-100 rounded-md transition-colors"
            >
              <Settings className="w-4 h-4" />
              <span>Settings</span>
            </button>
            
            <button
              onClick={handleLogout}
              className="w-full flex items-center space-x-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-md transition-colors"
            >
              <LogOut className="w-4 h-4" />
              <span>Sign Out</span>
            </button>
          </div>
        </div>
      )}

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-96 max-w-90vw">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium">Change Profile Picture</h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setShowUploadModal(false);
                  setUploadFile(null);
                  setUploadPreview(null);
                }}
                className="h-6 w-6 p-0"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>

            <div className="space-y-4">
              {/* Current Profile Picture */}
              <div className="flex items-center justify-center">
                <div className="w-20 h-20 rounded-full overflow-hidden border-2 border-slate-200">
                  {uploadPreview ? (
                    <img
                      src={uploadPreview}
                      alt="Preview"
                      className="w-full h-full object-cover"
                    />
                  ) : profileImageUrl ? (
                    <img
                      src={profileImageUrl}
                      alt={user?.full_name}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                  ) : null}
                  <div className={`w-20 h-20 bg-gradient-to-r from-blue-500 to-indigo-500 flex items-center justify-center text-white text-lg font-medium ${uploadPreview || profileImageUrl ? 'hidden' : 'flex'}`}>
                    {user?.full_name?.charAt(0) || '?'}
                  </div>
                </div>
              </div>

              {/* File Upload */}
              <div>
                <input
                  type="file"
                  accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                  onChange={(e) => handleFileSelect(e.target.files[0])}
                  className="hidden"
                  id="profile-upload"
                />
                <label
                  htmlFor="profile-upload"
                  className="block w-full text-center px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-medium cursor-pointer hover:bg-blue-700 transition-colors"
                >
                  Choose New Picture
                </label>
              </div>

              {/* Action Buttons */}
              {uploadPreview && (
                <div className="flex gap-2">
                  <Button
                    onClick={handleUpload}
                    disabled={isUploading}
                    className="flex-1"
                  >
                    {isUploading ? 'Uploading...' : 'Upload'}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setUploadFile(null);
                      setUploadPreview(null);
                    }}
                    disabled={isUploading}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                </div>
              )}

              <div className="text-xs text-slate-500 text-center">
                No file size limit<br />
                Supported formats: JPEG, PNG, GIF, WebP
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Close dropdown when clicking outside */}
      {showDropdownMenu && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setShowDropdownMenu(false)}
        />
      )}

      {/* Settings Modal */}
      <SettingsModal
        isOpen={showSettingsModal}
        onClose={() => setShowSettingsModal(false)}
      />
    </div>
  );
};

export default ProfilePicture;